import React from 'react'
import { useDispatch } from 'react-redux'
import styled from '@emotion/styled'
import { hexColors } from '@mobi/settings'
import { InfoBox } from '@mobi/component-library/Common/V2/Display/InfoBox'
import { colors, radius, spacing, font, shadow } from '@mobi/component-library/Theme/Common'
import { Icon as LegacyIcon } from '@mobi/component-library/Common/Icon'
import type { BetSlipItem, BetSpecialOffer, FobSelection } from '@mobi/betslip/types'
import { PromoBanner } from '@mobi/betslip/Components/Common/PromoBanner'
import { calculateProjectedReward, isSuperPickEligible } from '@mobi/betslip/helpers/superPicks'
import { setSelectedSpecialOffer } from '@mobi/betslip/Store/Bets'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { selectBonusCashCampaigns } from '@mobi/betslip/Store/Workflow/selectors'
import { getInvalidSuperPickErrorMessage } from '@mobi/betslip/helpers/getInvalidSuperPickErrorMessage'
import { ShareBetContext } from '@mobi/betslip/context'

const enum LocalConstants {
  ActiveButtonClass = 'js-superpicks__button--active',
}

export const SuperPicks: React.FC<
  Pick<BetSlipItem, 'specialOffers' | 'id' | 'selectedSuperPickOffer' | 'investment'> &
    Pick<FobSelection, 'winPrice' | 'placePrice'>
> = ({ specialOffers, id, winPrice, placePrice, selectedSuperPickOffer, investment }) => {
  const dispatch = useDispatch()
  const isShareDisplay = React.useContext(ShareBetContext)

  const workflowStatus = useAppSelector(state => state.betslip.workflow.currentStatus)
  const bonusCashCampaigns = useAppSelector(selectBonusCashCampaigns)

  const hasInvalidSuperPick =
    selectedSuperPickOffer &&
    !isSuperPickEligible(selectedSuperPickOffer, {
      win: investment.win.value,
      place: investment.place.value,
    })

  const invalidSuperPickErrorMessage =
    selectedSuperPickOffer && getInvalidSuperPickErrorMessage(selectedSuperPickOffer)

  if (workflowStatus !== 'ready' || isShareDisplay) {
    return selectedSuperPickOffer ? (
      <PromoBanner promoType='SuperPick' text={selectedSuperPickOffer.title as string} />
    ) : null
  }

  return (
    <div>
      <ContainerStyled>
        <h2>
          <span>
            <LegacyIcon type='specials' color={hexColors.lightOrange} size='1.6rem' /> SuperPicks
          </span>
        </h2>

        <div>
          {specialOffers.map(specialOffer => {
            const projectedReward = calculateProjectedReward(specialOffer, investment, {
              winPrice,
              placePrice,
            })
            const selectedSuperPickSeq = selectedSuperPickOffer?.specialSeq
            const isSelected = specialOffer.specialSeq === selectedSuperPickSeq
            const isInsuranceOffer = specialOffer.specialTypeCode === 'WBI'
            const maxReward = getMaxReward(specialOffer)

            return (
              <div key={specialOffer.title}>
                <button
                  className={isSelected ? LocalConstants.ActiveButtonClass : ''}
                  onClick={() =>
                    dispatch(setSelectedSpecialOffer({ id, specialOffer, bonusCashCampaigns }))
                  }
                  type='button'
                >
                  {specialOffer.title}
                </button>

                {maxReward && (
                  <span>
                    <strong key='first'>{`${isInsuranceOffer ? 'Money Back: ' : ''} $${projectedReward}`}</strong>{' '}
                    {maxReward}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </ContainerStyled>

      {hasInvalidSuperPick && (
        <InfoBox color='red' size='sm'>
          {invalidSuperPickErrorMessage}
        </InfoBox>
      )}
    </div>
  )
}

// =============
// Local Helpers
// =============

const getMaxReward = (specialOffer: BetSpecialOffer): string => {
  if (!specialOffer.elements) return ''

  const nonBoostElement = specialOffer.elements.find(
    (element: NonNullable<BetSpecialOffer['elements']>[0]) => !element.isBoost
  )
  if (!nonBoostElement) return ''

  return nonBoostElement.maxReward ? ` up to $${nonBoostElement.maxReward}` : ''
}

// ======
// Styles
// ======

const ContainerStyled = styled.div({
  '--light-color': colors.orange[50],
  '--dark-color': colors.black,

  overflow: 'hidden',
  backgroundColor: colors.white,
  borderRadius: radius.md,
  border: '0.1rem solid var(--light-color)',

  // Header
  '> h2': {
    margin: 0,
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '4rem',
    padding: `${spacing.smx2} ${spacing.sm} ${spacing.smx2} ${spacing.md}`,
    background: 'var(--light-color)',
    textShadow: 'none',
    textTransform: 'unset',
    border: 0,

    // Title Text
    '> span:first-of-type': {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      gap: spacing.smx1,
      fontFamily: font.family.primary,
      fontSize: font.size.lg.fontSize,
      letterSpacing: font.size.lg.letterSpacing,
      lineHeight: font.size.lg.lineHeight,
      color: colors.black,
      fontWeight: font.weight.medium,
      textAlign: 'left',
    },
  },

  // Buttons Wrapper
  '> div': {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: 'minmax(0, 1fr)',
    gap: spacing.smx1,
    padding: spacing.smx1,

    // Individual Button
    '> div': {
      display: 'flex',
      flexDirection: 'column',

      '> button': {
        height: '4.8rem',
        borderWidth: '0.5px',
        borderStyle: 'solid',
        borderColor: colors.neutral[300],
        color: colors.neutral[900],
        backgroundColor: colors.white,
        transition: 'background-color ease 300ms',
        WebkitTapHighlightColor: 'transparent',
        touchAction: 'manipulation',
        boxShadow: shadow.xs,
        borderRadius: radius.md,
        fontFamily: font.family.primary,
        fontSize: font.size.md.fontSize,
        letterSpacing: font.size.md.letterSpacing,
        lineHeight: 1.1,
        fontWeight: font.weight.medium,
        cursor: 'pointer',

        '@media (max-width: 360px)': {
          fontSize: font.size.sm.fontSize,
        },

        [`&.${LocalConstants.ActiveButtonClass}`]: {
          backgroundColor: hexColors.lightOrange,
          borderColor: colors.neutral[100],
          color: hexColors.darkOrange,
          boxShadow: 'none',
        },
      },

      '> span': {
        textAlign: 'center',
        fontFamily: font.family.primary,
        fontSize: font.size.sm.fontSize,
        fontWeight: font.weight.regular,
        color: colors.neutral[800],
        marginBottom: '-0.4rem',

        strong: { fontWeight: font.weight.medium },
      },
    },
  },
})
